import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, combineLatest, filter, map, switchMap, takeUntil } from 'rxjs';
import { PortalAuthService } from 'src/app/core/auth/portal-auth.service';
import { AdminApiService, ContactRequest } from 'src/app/core/services/admin-api.service';
import { MenuConfigService } from 'src/app/core/services/menu-config.service';
import { SeoService } from 'src/app/core/services/seo.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { RouterLink } from '@angular/router';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';

/**
 * Options carry translation KEYS, not resolved strings. Resolving them in the constructor
 * with translate.instant() printed the raw key whenever the HTTP-loaded translation file
 * had not arrived yet (a cold load or direct navigation), and it also froze the labels in
 * whatever language was active at construction time. The template pipes them instead, which
 * waits for the file and re-renders on every language switch.
 */
interface ContactType {
  id: string;
  labelKey: string;
  icon: string;
  hintKey: string;
  /** Subject sent when the user leaves the subject field empty. */
  subjectKey: string;
}

interface ProductOption {
  id: string;
  labelKey: string;
  /** Fallback for "all products", which is a bucket rather than a product. */
  icon: string;
  /** Real brand mark (assets/icon/<product>-mark.png); takes precedence over `icon`. */
  iconSrc?: string;
}

const CONTACT_TYPES: readonly ContactType[] = [
  { id: 'trial', icon: 'rocket-outline', labelKey: 'contact.typeTrialLabel', hintKey: 'contact.typeTrialHint', subjectKey: 'contact.subjectTrial' },
  { id: 'general', icon: 'chatbubble-outline', labelKey: 'contact.typeGeneralLabel', hintKey: 'contact.typeGeneralHint', subjectKey: 'contact.subjectGeneral' },
  { id: 'partner', icon: 'briefcase-outline', labelKey: 'contact.typePartnerLabel', hintKey: 'contact.typePartnerHint', subjectKey: 'contact.subjectPartner' },
];

const PRODUCTS: readonly ProductOption[] = [
  { id: 'all', icon: 'apps-outline', labelKey: 'contact.productAll' },
  { id: 'portal', icon: 'grid-outline', iconSrc: 'assets/icon/portal-mark.png', labelKey: 'contact.productPortal' },
  { id: 'chroniq', icon: 'time-outline', iconSrc: 'assets/icon/chroniq-mark.png', labelKey: 'contact.productChroniq' },
  { id: 'screendeck', icon: 'tv-outline', iconSrc: 'assets/icon/screendeck-mark.png', labelKey: 'contact.productScreenDeck' },
  { id: 'tablestack', icon: 'calendar-outline', iconSrc: 'assets/icon/tablestack-mark.png', labelKey: 'contact.productTableStack' },
  { id: 'menulink', icon: 'qr-code-outline', iconSrc: 'assets/icon/menulink-mark.png', labelKey: 'contact.productMenuLink' },
];

@Component({
    selector: 'app-contact',
    templateUrl: './contact.page.html',
    styleUrls: ['./contact.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PortalNavbarComponent,
        IonicModule,
        FormsModule,
        TooltipDirective,
        RouterLink,
        PortalFooterComponent,
        TranslatePipe,
    ],
})
export class ContactPage implements OnInit, OnDestroy {
  readonly contactTypes = CONTACT_TYPES;
  readonly products = PRODUCTS;

  /** Drives the header variant: app-portal-navbar while signed out; signed in, the
   *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
  isAuthenticated = false;

  /** A signed-in customer of a tenant with the ticket system gets pointed there: a ticket keeps
   *  its history and replies, while this form lands in the sales inbox. */
  hasSupport = false;

  selectedType: ContactType = CONTACT_TYPES[0];
  selectedProduct: ProductOption = PRODUCTS[0];

  get showProductSelector(): boolean {
    return this.selectedType.id === 'trial';
  }

  /**
   * A trial enquiry leads to a tenant being provisioned from the admin inbox, and that
   * needs a business name. It also makes the B2B nature of the offering explicit at the
   * point of enquiry (AGB § 1). Other enquiry types keep it optional.
   */
  get companyRequired(): boolean {
    return this.selectedType.id === 'trial';
  }

  form: ContactRequest = {
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  };

  loading = false;
  success = false;
  error = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private adminApi: AdminApiService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private seo: SeoService,
    private readonly portalAuth: PortalAuthService,
    private readonly userApi: UserApiService,
    private readonly menuConfig: MenuConfigService,
  ) { }

  ngOnInit(): void {
    this.seo.apply({ titleKey: 'mainauth.contact.navTitle', descriptionKey: 'mainauth.contact.lead', path: '/contact' });

    this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthenticated = auth;
      this.cdr.markForCheck();
    });

    combineLatest([this.portalAuth.isAuthenticated$, this.menuConfig.hasSupport$()]).pipe(
      map(([auth, support]) => auth && support),
      takeUntil(this.destroy$),
    ).subscribe(has => {
      this.hasSupport = has;
      this.cdr.markForCheck();
    });

    // A signed-in visitor should not have to type their own name and address again. Only empty
    // fields are filled, so nothing the user already typed is overwritten when the profile arrives.
    this.portalAuth.isAuthenticated$.pipe(
      filter(auth => auth),
      switchMap(() => this.userApi.getMe().pipe(catchError(() => EMPTY))),
      takeUntil(this.destroy$),
    ).subscribe(user => {
      const name = user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ');
      if (!this.form.name && name) this.form.name = name;
      if (!this.form.email && user.email) this.form.email = user.email;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectType(type: ContactType): void {
    this.selectedType = type;
    this.cdr.markForCheck();
  }

  selectProduct(product: ProductOption): void {
    this.selectedProduct = product;
    this.cdr.markForCheck();
  }

  submit(): void {
    if (!this.form.name || !this.form.email || !this.form.message) {
      this.error = this.translate.instant('contact.validationRequired');
      return;
    }
    if (!this.form.email.includes('@')) {
      this.error = this.translate.instant('contact.validationInvalidEmail');
      return;
    }
    if (this.companyRequired && !this.form.company?.trim()) {
      this.error = this.translate.instant('contact.validationCompanyRequired');
      return;
    }
    this.loading = true;
    this.error = '';
    // The language travels with the inquiry so the confirmation mail arrives in the language of the page.
    const request: ContactRequest = { ...this.form, subject: this.form.subject?.trim() || this.buildSubject(), language: this.translate.currentLang || this.translate.defaultLang || undefined };
    this.adminApi.submitContact(request).subscribe({
      next: () => {
        this.success = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = this.translate.instant('contact.sendError');
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Subject for an enquiry whose subject field was left empty: the type, plus the product for a trial. */
  private buildSubject(): string {
    const base = this.translate.instant(this.selectedType.subjectKey);
    if (this.showProductSelector && this.selectedProduct.id !== 'all') {
      return `${base}: ${this.translate.instant(this.selectedProduct.labelKey)}`;
    }
    return base;
  }
}
